import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Row, Col, Card, Button, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import {
    fetchCriteria,
    fetchCriteriaProjection,
    setSelectedCriterion,
    clearSelectedCriterion,
    fetchCriterionKeywords,
    clearCriteria,
    clearKeywords,
    deleteScoringCriterion,
    deleteScoringKeyword
} from "api/menu/hr/scoring-criteria/actions";

import { closeCustomModal, openCustomModal } from "components/custom/modals";

const CriterionModal = React.lazy(() => import("./criterion-modal"));
const KeywordModal = React.lazy(() => import("./keyword-modal"));

class ScoringCriteriaManager extends Component {
    componentDidMount() {
        this.props.fetchCriteria();
    }

    componentWillUnmount() {
        this.props.clearCriteria();
        this.props.clearKeywords();
        this.props.clearSelectedCriterion();
    }

    handleCriterionSelect = (criterionId) => {
        this.props.setSelectedCriterion(criterionId);
        this.props.fetchCriterionKeywords(criterionId);
    };

    handleCriterionDeselect = () => {
        this.props.clearSelectedCriterion();
        this.props.clearKeywords();
    };

    // Criterion CRUD handlers
    openCriterionModal = (criterionId = null) => {
        const modalTitle = criterionId ? "Edit" : "Add";
        const modalObj = {
            id: `criterion-modal-${criterionId || 'new'}`,
            header: `${modalTitle} Scoring Criterion`,
            modalSettings: { size: "lg" },
            children: (
                <Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <CriterionModal
                        criterionId={criterionId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSaved={(newCriterion) => {
                            this.props.fetchCriteria();
                            if (newCriterion && newCriterion.criterionID) {
                                this.handleCriterionSelect(newCriterion.criterionID);
                            }
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    deleteCriterion = (criterionId, category) => {
        this.props.deleteScoringCriterion(criterionId, category, () => {
            this.props.fetchCriteria();
            if (this.props.selectedCriterionId === criterionId) {
                this.handleCriterionDeselect();
            }
        });
    };

    // Keyword CRUD handlers
    openKeywordModal = (keywordId = null) => {
        if (!this.props.selectedCriterionId) return;
        
        const modalTitle = keywordId ? "Edit" : "Add";
        const modalObj = {
            id: `keyword-modal-${keywordId || 'new'}`,
            header: `${modalTitle} Keyword`,
            modalSettings: { size: "md" },
            children: (
                <Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <KeywordModal
                        keywordId={keywordId}
                        criterionId={this.props.selectedCriterionId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSaved={() => {
                            this.props.fetchCriterionKeywords(this.props.selectedCriterionId);
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    deleteKeyword = (keywordId, text) => {
        this.props.deleteScoringKeyword(keywordId, text, this.props.selectedCriterionId, () => {
            this.props.fetchCriterionKeywords(this.props.selectedCriterionId);
        });
    };

    getTypeBadgeVariant = (type) => {
        switch(type?.toLowerCase()) {
            case 'critical': return 'primary'; // blue
            case 'additional': return 'success'; // green
            case 'negative': return 'danger'; // red
            default: return 'secondary'; // gray for unknown types
        }
    };

    getScoreBadgeVariant = (score) => {
        const numScore = Number(score);
        if (numScore > 0) return 'success'; // green for positive
        if (numScore < 0) return 'danger'; // red for negative
        return 'secondary'; // gray for zero
    };

    formatScoreDisplay = (score) => {
        const numScore = Number(score) || 0;
        if (numScore > 0) return `+${numScore}`;
        if (numScore < 0) return `${numScore}`;
        return '0';
    };

    getTypePriority = (type) => {
        switch(type?.toLowerCase()) {
            case 'critical': return 1;
            case 'additional': return 2;
            case 'negative': return 3;
            default: return 4; // unknown types go last
        }
    };

    sortCriteria = (criteria) => {
        return [...criteria].sort((a, b) => {
            // First sort by type priority
            const typePriorityA = this.getTypePriority(a.type);
            const typePriorityB = this.getTypePriority(b.type);
            
            if (typePriorityA !== typePriorityB) {
                return typePriorityA - typePriorityB;
            }
            
            // Then sort by ID
            return a.criterionID - b.criterionID;
        });
    };

    sortKeywords = (keywords) => {
        return [...keywords].sort((a, b) => {
            return (a.text || '').toLowerCase().localeCompare((b.text || '').toLowerCase());
        });
    };

    renderCriteriaList() {
        const { criteria, loading, selectedCriterionId, keywords, keywordCounts } = this.props;

        if (loading.criteria) {
            return (
                <div className="text-center p-4">
                    <FontAwesomeIcon icon="spinner" spin size="2x" />
                    <div className="mt-2">Loading criteria...</div>
                </div>
            );
        }

        if (!criteria.length) {
            return (
                <div className="text-center p-4">
                    <FontAwesomeIcon icon="search" size="2x" className="text-muted mb-2" />
                    <div className="text-muted">No scoring criteria found</div>
                    <Button variant="primary" className="mt-3" onClick={() => this.openCriterionModal()}>
                        <FontAwesomeIcon icon="plus" className="mr-2" />
                        Add First Criterion
                    </Button>
                </div>
            );
        }

        const sortedCriteria = this.sortCriteria(criteria);

        return (
            <div>
                {sortedCriteria.map(criterion => (
                    <Card 
                        key={criterion.criterionID} 
                        className={`mb-2 cursor-pointer ${selectedCriterionId === criterion.criterionID ? 'border-primary bg-light' : ''}`}
                        onClick={() => this.handleCriterionSelect(criterion.criterionID)}
                    >
                        <Card.Body className="py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-1" style={{ fontSize: "1.5rem" }}>{criterion.category}</h6>
                                    <div className="d-flex align-items-center">
                                        <Badge 
                                            variant={this.getTypeBadgeVariant(criterion.type)} 
                                            style={{ fontSize: "1.1rem", padding: "6px 12px", marginRight: "8px" }}
                                        >
                                            {criterion.type || 'Unknown'}
                                        </Badge>
                                        {!criterion.enabled && <Badge variant="warning" style={{ fontSize: "1.1rem", padding: "6px 12px" }}>Disabled</Badge>}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="mb-2">
                                        <Button size="sm" variant="outline-secondary" className="mr-1" onClick={(e) => {
                                            e.stopPropagation();
                                            this.openCriterionModal(criterion.criterionID);
                                        }}>
                                            <FontAwesomeIcon icon="pen" />
                                        </Button>
                                        <Popconfirm
                                            title={`Delete criterion "${criterion.category}"? This will also delete all its keywords.`}
                                            onConfirm={(e) => {
                                                e.stopPropagation();
                                                this.deleteCriterion(criterion.criterionID, criterion.category);
                                            }}
                                            placement="bottomRight"
                                            okText="Yes"
                                            cancelText="Cancel"
                                        >
                                            <Button size="sm" variant="outline-danger" onClick={(e) => e.stopPropagation()}>
                                                <FontAwesomeIcon icon="times" />
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: "1.2rem" }}>
                                        {selectedCriterionId === criterion.criterionID 
                                            ? `${keywords.length} keywords loaded`
                                            : keywordCounts[criterion.criterionID] !== undefined
                                            ? `${keywordCounts[criterion.criterionID]} keywords`
                                            : "Click to manage keywords"
                                        }
                                    </small>
                                    <FontAwesomeIcon 
                                        icon={selectedCriterionId === criterion.criterionID ? "chevron-down" : "chevron-right"} 
                                        className="text-muted" 
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    }

    renderKeywordsList() {
        const { keywords, loading, selectedCriterionId } = this.props;

        if (!selectedCriterionId) {
            return (
                <div className="text-center p-5">
                    <FontAwesomeIcon icon="arrow-left" size="2x" className="text-muted mb-3" />
                    <h5 className="text-muted">Select a Criterion</h5>
                    <p className="text-muted">Choose a scoring criterion from the left panel to manage its keywords.</p>
                </div>
            );
        }

        if (loading.keywords) {
            return (
                <div className="text-center p-4">
                    <FontAwesomeIcon icon="spinner" spin size="2x" />
                    <div className="mt-2">Loading keywords...</div>
                </div>
            );
        }

        if (!keywords.length) {
            return (
                <div className="text-center p-4">
                    <FontAwesomeIcon icon="key" size="2x" className="text-muted mb-2" />
                    <div className="text-muted">No keywords found for this criterion</div>
                    <Button variant="primary" className="mt-3" onClick={() => this.openKeywordModal()}>
                        <FontAwesomeIcon icon="plus" className="mr-2" />
                        Add First Keyword
                    </Button>
                </div>
            );
        }

        const sortedKeywords = this.sortKeywords(keywords);

        return (
            <div>
                {sortedKeywords.map(keyword => (
                    <Card key={keyword.keywordID} className="mb-2">
                        <Card.Body className="py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="font-weight-medium" style={{ fontSize: "1.5rem" }}>{keyword.text}</span>
                                    <div className="mt-2 d-flex align-items-center flex-wrap">
                                        <Badge 
                                            variant={this.getScoreBadgeVariant(keyword.score)} 
                                            style={{ fontSize: "1rem", padding: "4px 8px", marginRight: "8px" }}
                                        >
                                            {this.formatScoreDisplay(keyword.score)}
                                        </Badge>
                                        {keyword.autoReject && (
                                            <Badge variant="warning" style={{ fontSize: "0.9rem", padding: "3px 6px", marginRight: "8px" }}>
                                                Auto-Reject
                                            </Badge>
                                        )}
                                        {!keyword.enabled && (
                                            <Badge variant="secondary" style={{ fontSize: "0.9rem", padding: "3px 6px" }}>
                                                Disabled
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <Button size="sm" variant="outline-secondary" className="mr-1" onClick={() => this.openKeywordModal(keyword.keywordID)}>
                                        <FontAwesomeIcon icon="pen" />
                                    </Button>
                                    <Popconfirm
                                        title={`Delete keyword "${keyword.text}"?`}
                                        onConfirm={() => this.deleteKeyword(keyword.keywordID, keyword.text)}
                                        placement="bottomRight"
                                        okText="Yes"
                                        cancelText="Cancel"
                                    >
                                        <Button size="sm" variant="outline-danger">
                                            <FontAwesomeIcon icon="times" />
                                        </Button>
                                    </Popconfirm>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    }

    render() {
        const { selectedCriterionId, criteria } = this.props;
        const selectedCriterion = criteria.find(c => c.criterionID === selectedCriterionId);

        return (
            <div className="dashboard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>Scoring Criteria</h3>
                    <Button variant="primary" onClick={() => this.openCriterionModal()}>
                        <FontAwesomeIcon icon="plus" className="mr-2" />
                        Add Criterion
                    </Button>
                </div>

                <Row>
                    <Col md={5}>
                        <Card style={{ height: "calc(100vh - 200px)" }}>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Criteria</h5>
                                <small className="text-muted">{this.props.criteria.length} total</small>
                            </Card.Header>
                            <Card.Body style={{ height: "calc(100vh - 260px)", overflowY: "auto" }}>
                                {this.renderCriteriaList()}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={7}>
                        <Card style={{ height: "calc(100vh - 200px)" }}>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center mb-1">
                                        <h5 className="mb-0 mr-2">
                                            {selectedCriterion ? `Keywords: ${selectedCriterion.category}` : "Keywords"}
                                        </h5>
                                        {selectedCriterion && (
                                            <Badge 
                                                variant={this.getTypeBadgeVariant(selectedCriterion.type)} 
                                                style={{ fontSize: "0.9rem", padding: "4px 8px" }}
                                            >
                                                {selectedCriterion.type || 'Unknown'}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                {selectedCriterionId && (
                                    <div>
                                        <Button size="sm" variant="outline-secondary" className="mr-2" onClick={this.handleCriterionDeselect}>
                                            <FontAwesomeIcon icon="times" />
                                        </Button>
                                        <Button size="sm" variant="primary" onClick={() => this.openKeywordModal()}>
                                            <FontAwesomeIcon icon="plus" className="mr-1" />
                                            Add Keyword
                                        </Button>
                                    </div>
                                )}
                            </Card.Header>
                            <Card.Body style={{ height: "calc(100vh - 260px)", overflowY: "auto" }}>
                                {this.renderKeywordsList()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

ScoringCriteriaManager.propTypes = {
    criteria: PropTypes.array.isRequired,
    keywords: PropTypes.array.isRequired,
    keywordCounts: PropTypes.object.isRequired,
    selectedCriterionId: PropTypes.number,
    loading: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    fetchCriteria: PropTypes.func.isRequired,
    setSelectedCriterion: PropTypes.func.isRequired,
    clearSelectedCriterion: PropTypes.func.isRequired,
    fetchCriterionKeywords: PropTypes.func.isRequired,
    clearCriteria: PropTypes.func.isRequired,
    clearKeywords: PropTypes.func.isRequired,
    deleteScoringCriterion: PropTypes.func.isRequired,
    deleteScoringKeyword: PropTypes.func.isRequired,
    openCustomModal: PropTypes.func.isRequired,
    closeCustomModal: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    criteria: state.scoringCriteria.criteria,
    keywords: state.scoringCriteria.keywords,
    keywordCounts: state.scoringCriteria.keywordCounts,
    selectedCriterionId: state.scoringCriteria.selectedCriterionId,
    loading: state.scoringCriteria.loading,
    errors: state.scoringCriteria.errors
});

const mapDispatchToProps = dispatch => ({
    fetchCriteria: bindActionCreators(fetchCriteria, dispatch),
    fetchCriteriaProjection: bindActionCreators(fetchCriteriaProjection, dispatch),
    setSelectedCriterion: bindActionCreators(setSelectedCriterion, dispatch),
    clearSelectedCriterion: bindActionCreators(clearSelectedCriterion, dispatch),
    fetchCriterionKeywords: bindActionCreators(fetchCriterionKeywords, dispatch),
    clearCriteria: bindActionCreators(clearCriteria, dispatch),
    clearKeywords: bindActionCreators(clearKeywords, dispatch),
    deleteScoringCriterion: bindActionCreators(deleteScoringCriterion, dispatch),
    deleteScoringKeyword: bindActionCreators(deleteScoringKeyword, dispatch),
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ScoringCriteriaManager);
